import { useEffect, useMemo } from 'react'

import { addUploaderTracking } from './addUploaderTracking'
import { createUppy } from './createUppy'
import { useS3Signature } from './getS3Signature'
import { Uploader } from './Uploader'
import type { UploaderEvents } from './Uploader'
import { defaultRestrictions } from './values/UploaderRestrictions'
import type { UploaderRestrictions } from './values/UploaderRestrictions'

/**
 * Returns an uploader instance for the given id with
 * uploader events bound to the redux store.
 * It also returns the current state of the uploader
 * and some callbacks.
 */
export const useUploader = ({
  id,
  fileRestrictions = defaultRestrictions,
  eventHandlers,
  getCustomCount,
  userId,
  organizationId,
}: {
  id: string
  fileRestrictions?: UploaderRestrictions
  eventHandlers?: Partial<UploaderEvents>
  getCustomCount?: () => number
  userId: string
  organizationId: string
}): Uploader => {
  const getS3Signature = useS3Signature()

  const uploader = useMemo(() => {
    const uppyInstance = createUppy({
      id,
      organizationId,
      userId,
      getS3Signature,
    })

    const uploaderInstance = new Uploader(
      id,
      organizationId,
      uppyInstance,
      fileRestrictions,
      getCustomCount,
    )

    if (eventHandlers) {
      Object.keys(eventHandlers).forEach((eventName) => {
        const handler = eventHandlers[eventName as keyof UploaderEvents]
        if (handler) {
          uploaderInstance.on(eventName as keyof UploaderEvents, handler)
        }
      })
    }

    addUploaderTracking(uploaderInstance)

    return uploaderInstance
  }, [
    id,
    userId,
    organizationId,
    fileRestrictions,
    getS3Signature,
    getCustomCount,
    eventHandlers,
  ])

  useEffect(() => {
    return () => {
      uploader.close()
    }
  }, [])

  return uploader
}
